import { NativeEventListener } from "src/api/NativeEventListener";
import { ScreenActivityCollector } from "src/collector/ScreenActivityCollector";
import { SecurityCollector } from "src/collector/SecurityCollector";
import { DataPayload } from "src/di/DataPayload";
import { ClickListener } from "src/listener/ClickListener";
import { CopyPasteListener } from "src/listener/CopyPasteListener";
import { EventListener as TrackerEventListener } from "src/listener/EventListener";
import { FormInteractionListener } from "src/listener/FormInteractionListener";
import { IdleListener } from "src/listener/IdleListener";
import { PageViewListener } from "src/listener/PageViewListener";
import { ScrollListener } from "src/listener/ScrollListener";
import { VisibilityListener } from "src/listener/VisibilityListener";

import { BaseDataCollector } from "src/core/base/BaseDataCollector";
import { DataCollector } from "src/core/contract/DataCollector";
import { Event } from "src/core/model/Event";
import { NativeEventData } from "src/core/model/NativeEventData";
import { ConfigItem, TriggerConfig } from "src/core/model/TrackerConfig";
import { TrackerEventType } from "src/core/model/TrackerEventType";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { EventService } from "src/service/EventService";
import { Logger } from "src/util/Logger";

import { FilterEngine } from "./FilterEngine";

/**
 * Coordinates native event listeners and manages their lifecycle.
 * Mirrors Android EventCoordinator — adapted for browser environment.
 */
export class EventCoordinator {
  private listeners: TrackerEventListener[] = [];
  private eventService: EventService;
  private collectors: DataCollector[];
  private payload: DataPayload;
  private screenActivityCollector: ScreenActivityCollector;
  private securityCollector: SecurityCollector;
  private userDefinedValuesProvider: (() => Record<string, unknown>) | null = null;
  private stopped = true;
  private nativeEventListener: NativeEventListener | null = null;

  // RISK_ALERT auto-trigger state
  private riskAlertIntervalId: ReturnType<typeof setInterval> | null = null;
  private lastRiskAlertTs = 0;

  constructor(
    eventService: EventService,
    collectors: DataCollector[],
    payload: DataPayload,
    screenActivityCollector: ScreenActivityCollector,
    securityCollector: SecurityCollector,
  ) {
    this.eventService = eventService;
    this.collectors = collectors;
    this.payload = payload;
    this.screenActivityCollector = screenActivityCollector;
    this.securityCollector = securityCollector;
  }

  setUserDefinedValuesProvider(provider: () => Record<string, unknown>): void {
    this.userDefinedValuesProvider = provider;
  }

  start(): void {
    Logger.debug("Starting EventCoordinator");
    this.stopped = false;
    this.initializeListeners();
    this.startRiskAlertEvaluator();

    // Trigger initial PAGE_VIEW event
    this.triggerNativeEvent(TrackerEventType.PAGE_VIEW);

    Logger.debug("EventCoordinator started with", this.listeners.length, "listeners");
  }

  stop(): void {
    Logger.debug("Stopping EventCoordinator");
    this.stopped = true;

    if (this.riskAlertIntervalId !== null) {
      clearInterval(this.riskAlertIntervalId);
      this.riskAlertIntervalId = null;
    }

    for (const listener of this.listeners) {
      try {
        listener.stop();
      } catch (error) {
        Logger.error("Error stopping listener:", error);
      }
    }

    this.listeners = [];
    this.nativeEventListener = null;
    Logger.debug("EventCoordinator stopped");
  }

  setNativeEventListener(listener: NativeEventListener | null): void {
    this.nativeEventListener = listener;
  }

  collectAllVariablesNow(): Map<TrackerVariableType, unknown> {
    const allVariables = new Map<TrackerVariableType, unknown>();

    // Get variables from payload
    const payloadVars = this.payload.getCollectedVariables();
    for (const [k, v] of payloadVars) {
      allVariables.set(k, v);
    }

    // Collect from all data collectors
    this.collectFromAllCollectors(allVariables);

    return allVariables;
  }

  triggerNativeEvent(type: TrackerEventType): void {
    if (this.stopped) {
      Logger.warn("Ignoring native event (coordinator stopped):", type);
      return;
    }

    try {
      this.triggerEventInternal(type, null, null);
    } catch (error) {
      Logger.error("Error in triggerNativeEvent:", error);
    }
  }

  triggerCustomEvent(eventName: string, args: Record<string, unknown>): void {
    try {
      this.triggerEventInternal(TrackerEventType.CUSTOM, eventName, args);
    } catch (error) {
      Logger.error("Error in triggerCustomEvent:", error);
    }
  }

  // === Internal ===

  private triggerEventInternal(
    eventType: TrackerEventType,
    customEventName: string | null,
    customArguments: Record<string, unknown> | null,
  ): void {
    const rootConfig = this.payload.getTrackerConfig();
    const isCustomEvent = eventType === TrackerEventType.CUSTOM;

    if (!rootConfig) {
      Logger.error("Config not initialized, cannot trigger event");
      return;
    }

    Logger.debug(isCustomEvent ? `Custom event triggered: ${customEventName}` : `Native event triggered: ${eventType}`);

    // Register page view for risk metrics
    if (eventType === TrackerEventType.PAGE_VIEW) {
      this.securityCollector.registerPageView();
      this.screenActivityCollector.resetTimer();
      this.screenActivityCollector.startTimer();
    }

    // Collect all variables
    const allConfigVariables = this.collectAllConfigVariables();

    // Inject user-defined values into CUSTOM_TRIGGER_ARGS for all events
    const userValues = this.userDefinedValuesProvider?.() ?? {};
    if (isCustomEvent && customEventName) {
      // Merge: custom arguments take precedence, then user-defined values
      const merged = { ...userValues, ...customArguments };
      if (Object.keys(merged).length > 0) {
        allConfigVariables.set(TrackerVariableType.CUSTOM_TRIGGER_ARGS, merged);
      }
    } else if (Object.keys(userValues).length > 0) {
      allConfigVariables.set(TrackerVariableType.CUSTOM_TRIGGER_ARGS, userValues);
    }

    // Process each config
    for (const config of rootConfig.configs) {
      try {
        const triggerConfig = this.findMatchingTrigger(config, eventType, customEventName);
        if (!triggerConfig) continue;

        // Set current trigger name
        allConfigVariables.set(TrackerVariableType.CURRENT_TRIGGER_NAME, triggerConfig.name);

        // Check trigger filters
        if (!this.passesTriggerFilters(config, triggerConfig, allConfigVariables)) {
          Logger.debug("Trigger", triggerConfig.name, "blocked by filters");
          continue;
        }

        // Resolve screen activity variables (URL, Element, Cookie, JS)
        const screenVars = this.screenActivityCollector.resolveConfigVariables(config);
        for (const [k, v] of screenVars) {
          allConfigVariables.set(k, v);
        }

        // Map variables to backend fields
        const variables = BaseDataCollector.mapVariablesToBackend(config, allConfigVariables);

        // Create and enqueue event
        const deviceId = this.payload.getDeviceId();
        const event: Event = {
          name: config.event.name,
          key: deviceId,
          variables,
        };

        this.eventService.enqueueEvent(event);
        Logger.debug("Event enqueued:", config.event.name);
      } catch (error) {
        Logger.error("Error processing config for event:", error);
      }
    }

    // Notify external listener
    if (this.nativeEventListener) {
      try {
        const eventData: NativeEventData = {
          type: eventType,
          args: Object.fromEntries(allConfigVariables),
          timestamp: Date.now(),
        };
        this.nativeEventListener.onNativeEvent(eventData);
      } catch (error) {
        Logger.error("Error notifying native event listener:", error);
      }
    }
  }

  private collectAllConfigVariables(): Map<TrackerVariableType, unknown> {
    const variables = new Map<TrackerVariableType, unknown>();

    // Get payload variables
    const payloadVars = this.payload.getCollectedVariables();
    for (const [k, v] of payloadVars) {
      variables.set(k, v);
    }

    // Collect from all data collectors
    this.collectFromAllCollectors(variables);

    return variables;
  }

  private collectFromAllCollectors(target: Map<TrackerVariableType, unknown>): void {
    for (const collector of this.collectors) {
      try {
        const collectedData = collector.collect();
        for (const [k, v] of collectedData) {
          target.set(k, v);
        }
      } catch (error) {
        Logger.error("Error collecting from collector:", error);
      }
    }
  }

  private findMatchingTrigger(
    config: ConfigItem,
    eventType: TrackerEventType,
    customEventName: string | null,
  ): TriggerConfig | null {
    if (eventType === TrackerEventType.CUSTOM && customEventName) {
      return config.triggers.find((t) => t.type === TrackerEventType.CUSTOM && t.name === customEventName) ?? null;
    }
    return config.triggers.find((t) => t.type === eventType) ?? null;
  }

  private passesTriggerFilters(
    config: ConfigItem,
    trigger: TriggerConfig,
    variables: Map<TrackerVariableType, unknown>,
  ): boolean {
    if (!trigger.filters || trigger.filters.length === 0) return true;

    // Build a string-keyed map for filter matching
    const filterData = new Map<string, unknown>();
    for (const varConfig of config.variables) {
      const type = varConfig.type as TrackerVariableType;
      const value = variables.get(type);
      if (value !== undefined) {
        filterData.set(varConfig.name, value);
      }
    }

    return FilterEngine.passesTriggerFilters(trigger.filters, filterData);
  }

  private initializeListeners(): void {
    const config = this.payload.getTrackerConfig();
    if (!config) {
      Logger.error("Config not initialized, cannot initialize listeners");
      return;
    }

    const triggerFn = (type: TrackerEventType) => this.triggerNativeEvent(type);

    // Register all web listeners
    this.listeners.push(new PageViewListener(triggerFn));
    this.listeners.push(new ClickListener(triggerFn));
    this.listeners.push(new ScrollListener(triggerFn));
    this.listeners.push(new VisibilityListener(triggerFn));
    this.listeners.push(new IdleListener(triggerFn));
    this.listeners.push(new FormInteractionListener(triggerFn));
    this.listeners.push(new CopyPasteListener(triggerFn));

    // Start only listeners whose event types have triggers in config
    for (const listener of this.listeners) {
      const hasTriggers = listener
        .getTypes()
        .some((type) => config.configs.some((c) => c.triggers.some((t) => t.type === type)));

      const hasVariables = listener
        .getVariableTypes()
        .some((varType) => config.configs.some((c) => Object.values(c.event.variableMappings).includes(varType)));

      if (!hasTriggers && !hasVariables) {
        Logger.debug("Skipping listener (not configured):", listener.constructor.name);
        continue;
      }

      try {
        listener.start();
        Logger.debug("Started listener:", listener.constructor.name);
      } catch (error) {
        Logger.error("Failed to start listener:", listener.constructor.name, error);
      }
    }
  }

  private startRiskAlertEvaluator(): void {
    const config = this.payload.getTrackerConfig();
    if (!config) return;

    // Only run if RISK_ALERT triggers exist in config
    const hasRiskAlertTrigger = config.configs.some((c) =>
      c.triggers.some((t) => t.type === TrackerEventType.RISK_ALERT),
    );
    if (!hasRiskAlertTrigger) return;

    this.riskAlertIntervalId = setInterval(() => {
      this.evaluateRiskAlert();
    }, 30_000);
  }

  private evaluateRiskAlert(): void {
    if (this.stopped) return;

    // 60s cooldown
    if (Date.now() - this.lastRiskAlertTs < 60_000) return;

    const vars = this.collectAllConfigVariables();
    const biometricScore = (vars.get(TrackerVariableType.BEHAVIORAL_BIOMETRIC_SCORE) as number) ?? 0;
    const remoteAccessScore = (vars.get(TrackerVariableType.REMOTE_ACCESS_SCORE) as number) ?? 0;

    const combinedScore = biometricScore * 0.6 + remoteAccessScore * 0.4;
    if (combinedScore > 0.6) {
      this.lastRiskAlertTs = Date.now();
      Logger.debug("RISK_ALERT triggered (combined score:", combinedScore, ")");
      this.triggerNativeEvent(TrackerEventType.RISK_ALERT);
    }
  }
}
